/**
 * ScenarioSummary: end-of-scenario recap card.
 *
 * Renders below the workflow / panel viewer once the scenario reaches a
 * meaningful terminal state (customer confirmed, balance settled, warning
 * resolved, booking drafted, etc.).  Shows:
 *
 *   1. A short "What just happened" recap in plain product-truth language.
 *   2. Optional related-scenario shortcut links so the promoter or business
 *      owner can jump into an adjacent workflow that naturally follows.
 *      Example: after a Messenger intake ends in "customer requested change"
 *      → link to the Date Change scenario.
 *
 * Audience-aware: the related links point to /promoter/demos/… when a
 * promoter is presenting, and /demo-business/scenarios/… when a business
 * owner is self-exploring.
 */

import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, RotateCw, List, Sparkles } from "lucide-react";
import { SCENARIOS } from "../scenarios";

export default function ScenarioSummary({
  summary, audience = "promoter", onReset, onChooseAnother,
}) {
  if (!summary) return null;
  const basePath = audience === "public" ? "/demo-business/scenarios"
                                          : "/promoter/demos";
  // Look up each related scenario so we can show its real title + subtitle.
  const relatedItems = (summary.related || [])
    .map((id) => SCENARIOS.find((s) => s.id === id))
    .filter(Boolean);

  return (
    <div data-testid="scenario-summary"
         className="mt-4 rounded-2xl border border-emerald-200 bg-white shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-emerald-50 to-emerald-100/40 px-4 sm:px-6 py-4 border-b border-emerald-200 flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-emerald-600 text-white grid place-items-center flex-shrink-0">
          <CheckCircle2 className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-800">
            Scenario recap
          </div>
          <h3 className="mt-0.5 font-display text-lg font-semibold text-slate-900 leading-snug">
            {summary.title || "What just happened"}
          </h3>
          <p className="mt-1 text-sm text-slate-700 leading-snug">{summary.what}</p>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-4 space-y-4">
        {relatedItems.length > 0 && (
          <div data-testid="scenario-summary-related">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500 inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Related scenarios
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {summary.relatedIntro || "Try one of these next: they naturally follow from what you just saw."}
            </p>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {relatedItems.map((s) => (
                <Link key={s.id} to={`${basePath}/${s.id}`}
                       data-testid={`scenario-summary-related-${s.id}`}
                       className="group flex items-start gap-3 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors p-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 grid place-items-center flex-shrink-0 group-hover:bg-emerald-100 group-hover:text-emerald-700">
                    <ArrowRight className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 leading-tight">{s.title}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500 leading-snug line-clamp-2">{s.subtitle}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
          {onReset && (
            <button onClick={onReset}
                    data-testid="scenario-summary-reset"
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
              <RotateCw className="w-3.5 h-3.5" /> Replay this scenario
            </button>
          )}
          {onChooseAnother && (
            <button onClick={onChooseAnother}
                    data-testid="scenario-summary-choose"
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
              <List className="w-3.5 h-3.5" /> Choose another
            </button>
          )}
          {audience === "public" && (
            <Link to="/register?ref=demo-scenarios"
                  data-testid="scenario-summary-signup"
                  className="ml-auto inline-flex items-center gap-1 text-sm px-4 py-2 rounded-full bg-[#1B4332] text-white font-semibold hover:bg-emerald-900">
              Start free for my business <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
